// main script

// panels — reusable sliding drawer system
// trigger: element or selector for the element that opens/closes the panel
// panel: element or selector for the panel element
// options:
//   withOverlay: bool — show a clickable overlay behind the panel (default: true)
//   closeBtn: selector — dedicated close element inside the panel (default: ".close-btn")
//   withHeaderPadding: bool — set padding-top: var(--header-height) on the panel (default: true)
function initPanel(trigger, panel, options) {
  const defaults = {
    withOverlay: true,
    closeBtn: '.close-btn',
    withHeaderPadding: true,
  }
  const opts = Object.assign({}, defaults, options)
  const triggerEl =
    typeof trigger === 'string' ? document.querySelector(trigger) : trigger
  const panelEl =
    typeof panel === 'string' ? document.querySelector(panel) : panel

  if (!triggerEl || !panelEl) return

  if (opts.withHeaderPadding) {
    panelEl.style.paddingTop = 'var(--header-height)'
  }

  // clean up previous bindings before re-init (breakpoint change)
  if (triggerEl._panelAbort) triggerEl._panelAbort.abort()
  const controller = new AbortController()
  triggerEl._panelAbort = controller
  const { signal } = controller

  function openPanel() {
    document.dispatchEvent(
      new CustomEvent('panel:closeOthers', { detail: { except: panelEl } })
    )
    panelEl.classList.add('open')
    triggerEl.classList.add('open')
    document.body.classList.add('panel-open')
    if (opts.withOverlay) {
      if (!document.getElementById('panel-overlay')) {
        document.body.insertAdjacentHTML(
          'beforeend',
          '<div id="panel-overlay"></div>'
        )
      }
      const overlay = document.getElementById('panel-overlay')
      overlay.classList.add('visible')
      overlay.addEventListener('click', closePanel, { once: true })
    }
  }

  function closePanel() {
    panelEl.classList.remove('open')
    triggerEl.classList.remove('open')
    document.body.classList.remove('panel-open')
    if (opts.withOverlay) {
      document.getElementById('panel-overlay')?.classList.remove('visible')
    }
  }

  document.addEventListener(
    'panel:closeOthers',
    (e) => {
      if (panelEl !== e.detail.except && panelEl.classList.contains('open')) {
        closePanel()
      }
    },
    { signal }
  )

  triggerEl.addEventListener(
    'click',
    (e) => {
      e.preventDefault()
      panelEl.classList.contains('open') ? closePanel() : openPanel()
    },
    { signal }
  )

  if (opts.closeBtn) {
    panelEl.querySelectorAll(opts.closeBtn).forEach((btn) => {
      btn.addEventListener('click', closePanel, { signal })
    })
  }
}

// panels — auto-init from data attributes on .panel-btn elements
// data-panel: target panel selector (fallback: href)
// data-with-overlay: bool
// data-close-btn: selector
// data-with-header-padding: bool
function initPanels() {
  document.querySelectorAll('.panel-btn').forEach((btn) => {
    const target = btn.dataset.panel || btn.getAttribute('href')
    const opts = {}
    if (btn.dataset.withOverlay !== undefined)
      opts.withOverlay = btn.dataset.withOverlay === 'true'
    if (btn.dataset.closeBtn !== undefined) opts.closeBtn = btn.dataset.closeBtn
    if (btn.dataset.withHeaderPadding !== undefined)
      opts.withHeaderPadding = btn.dataset.withHeaderPadding === 'true'
    initPanel(btn, target, opts)
  })
}

// hover menus — desktop only
// opens sub-menus on mouseenter with a short delay to avoid accidental triggers
// closes immediately on mouseleave
// signal: AbortSignal — tied to the shell lifecycle, cleaned up on breakpoint change
function initHoverMenus(signal) {
  const menuItems = document.querySelectorAll('#menu-container .menu-top-level')

  menuItems.forEach((item) => {
    const toggle = item.querySelector(':scope > .toggle')
    const submenu = item.querySelector(':scope > .sub-menu-container')
    if (!toggle || !submenu) return

    let hoverTimer

    item.addEventListener(
      'mouseenter',
      () => {
        hoverTimer = setTimeout(() => {
          menuItems.forEach((other) => {
            if (other !== item) {
              other
                .querySelector(':scope > .toggle')
                ?.classList.remove('active')
              other
                .querySelector(':scope > .toggle')
                ?.setAttribute('aria-expanded', 'false')
              other
                .querySelector(':scope > .sub-menu-container')
                ?.classList.remove('active')
            }
          })
          toggle.classList.add('active')
          toggle.setAttribute('aria-expanded', 'true')
          submenu.classList.add('active')
        }, 250)
      },
      { signal }
    )

    item.addEventListener(
      'mouseleave',
      () => {
        clearTimeout(hoverTimer)
        toggle.classList.remove('active')
        toggle.setAttribute('aria-expanded', 'false')
        submenu.classList.remove('active')
      },
      { signal }
    )
  })
}

// shell
let shellController = null

function initShell(mobile) {
  // clean up previous shell bindings on breakpoint change
  if (shellController) shellController.abort()
  shellController = new AbortController()
  const { signal } = shellController

  const header = document.getElementById('header')
  const main = document.querySelector('main')
  const menuContainer = document.getElementById('menu-container')
  const navTool = document.getElementById('nav-tool')

  if (mobile) {
    header.insertAdjacentElement('afterend', menuContainer)
    menuContainer.style.paddingTop = header.offsetHeight + 'px'
    menuContainer.style.paddingBottom =
      document.getElementById('mobile-user-btn').offsetHeight - 1 + 'px'
    initPanel('.menu-btn', '#menu-container', { withOverlay: false })
  } else {
    // desktop: nav between the left tools (search) and the right tools (account, cart)
    navTool.insertAdjacentElement('afterend', menuContainer)
    menuContainer.style.paddingTop = '0'
    // disable mobile menu panel behavior
    const menuBtn = document.querySelector('.menu-btn')
    if (menuBtn?._panelAbort) menuBtn._panelAbort.abort()
    // hover menus on desktop
    initHoverMenus(signal)
  }

  // measured once the nav is in place (inside the header on desktop)
  const headerHeight = header?.offsetHeight || 0
  const headerTop = header?.offsetTop || 0
  if (main) {
    main.style.paddingTop = headerHeight + 'px'
  }
  if (headerHeight + headerTop > 0) {
    document.documentElement.style.setProperty(
      '--header-height',
      headerHeight + headerTop + 'px'
    )
  }

  // open/close sub-menus on click
  // mobile: primary interaction / desktop: accessible fallback alongside hover
  document
    .querySelectorAll('#menu-container .menu-top-level > .toggle')
    .forEach((toggle) => {
      toggle.addEventListener(
        'click',
        function () {
          document
            .querySelectorAll('#menu-container .menu-top-level > .toggle')
            .forEach((other) => {
              if (other !== toggle) {
                other.setAttribute('aria-expanded', 'false')
                other.classList.remove('active')
                other.nextElementSibling?.classList.remove('active')
              }
            })
          const isOpen = toggle.classList.toggle('active')
          toggle.nextElementSibling?.classList.toggle('active')
          toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
        },
        { signal }
      )
    })
}

// price range — dual range slider, updates --min-pct / --max-pct on the container
// labels follow their respective thumb via absolute positioning + left: var(--X-pct)
function initPriceRange() {
  document.querySelectorAll('.price-range').forEach((container) => {
    const minInput = container.querySelector('.price-range-min')
    const maxInput = container.querySelector('.price-range-max')
    const minLabel = container.querySelector('.price-range-min-value')
    const maxLabel = container.querySelector('.price-range-max-value')
    const gap = parseInt(minInput.step) * 2 || 10

    function update() {
      let min = parseFloat(minInput.value)
      let max = parseFloat(maxInput.value)

      // enforce gap between thumbs
      if (min > max - gap) {
        minInput.value = max - gap
        min = max - gap
      }
      if (max < min + gap) {
        maxInput.value = min + gap
        max = min + gap
      }

      const range = parseFloat(maxInput.max) - parseFloat(minInput.min)
      const minPct = ((min - parseFloat(minInput.min)) / range) * 100
      const maxPct = ((max - parseFloat(minInput.min)) / range) * 100

      container.style.setProperty('--min-pct', minPct + '%')
      container.style.setProperty('--max-pct', maxPct + '%')

      // z-index: make min thumb grabbable when both are at the high end
      minInput.style.zIndex = minPct > 50 ? 3 : 1

      if (minLabel) minLabel.textContent = min + '€'
      if (maxLabel) maxLabel.textContent = max + '€'
    }

    minInput.addEventListener('input', update)
    maxInput.addEventListener('input', update)
    update()
  })
}

// tooltips — adjust horizontal position to stay within viewport
function initTooltips() {
  function adjustTooltip(tooltip) {
    tooltip.style.transform = ''
    const rect = tooltip.getBoundingClientRect()
    const margin = 8
    if (rect.right > window.innerWidth - margin) {
      const shift = rect.right - (window.innerWidth - margin)
      tooltip.style.transform = `translateX(calc(-50% - ${shift}px))`
    } else if (rect.left < margin) {
      const shift = margin - rect.left
      tooltip.style.transform = `translateX(calc(-50% + ${shift}px))`
    }
  }

  const allTooltips = []

  document.querySelectorAll('.tooltip-trigger').forEach((trigger) => {
    const tooltip = trigger.querySelector('.tooltip')
    if (!tooltip) return
    allTooltips.push(tooltip)
    // re-adjust on hover in case trigger has moved (e.g. inside a panel)
    trigger.addEventListener('mouseenter', () => adjustTooltip(tooltip))
    trigger.addEventListener('focusin', () => adjustTooltip(tooltip))
  })

  // pre-adjust all on init so position is correct before first hover
  allTooltips.forEach(adjustTooltip)
  // re-adjust on resize
  window.addEventListener('resize', () => allTooltips.forEach(adjustTooltip))
}

// accordions
function initAccordions() {
  document.querySelectorAll('.accordion dt').forEach((dt) => {
    // init aria-expanded based on current open/active state
    dt.setAttribute(
      'aria-expanded',
      dt.classList.contains('open') || dt.classList.contains('active')
        ? 'true'
        : 'false'
    )

    if (dt.classList.contains('active')) return

    dt.addEventListener('click', function (e) {
      // ignore clicks on links inside the dt (e.g. a panel trigger)
      if (e.target.closest('a')) return
      this.classList.toggle('open')
      this.setAttribute(
        'aria-expanded',
        this.getAttribute('aria-expanded') === 'true' ? 'false' : 'true'
      )
    })
  })
}

// RWD — re-init shell on breakpoint change (mobile: < 768px)
const mobileQuery = window.matchMedia('(max-width: 767px)')
mobileQuery.addEventListener('change', (e) => initShell(e.matches ? 1 : 0))

// init
document.addEventListener('DOMContentLoaded', () => {
  // tiles click — the whole card follows its first link
  document.querySelectorAll('.card').forEach((card) => {
    card.addEventListener('click', (e) => {
      // inner controls (buttons, links, form fields) keep their own behaviour
      if (e.target.closest('a, button, input, select, textarea, label')) return
      e.preventDefault()
      const link = card.querySelector('a')
      if (!link) return
      const url = link.getAttribute('href')
      if (!url) return
      if (link.getAttribute('target') === '_blank') {
        window.open(url, '_blank')
        return
      }
      window.location.href = url
    })
  })

  initShell(mobileQuery.matches ? 1 : 0)
  initPanels()
  initAccordions()
  initTooltips()
  initPriceRange()
})
